import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { 
  Search, Filter, ShoppingCart, Download, ChevronLeft, ChevronRight,
  X, Check, Database, Cpu, Car, FileText, Loader2, Package,
  CreditCard, Mail, AlertCircle
} from 'lucide-react';
import { BACKEND_URL } from '../config/api';
import Footer from '../components/Footer';

const API_URL = BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// Price constants
const MODIFIED_PRICE = 15.00;
const STOCK_PRICE = 7.50;

export default function FileStorePage() {
  // Get URL search params
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Initialize filters from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '');
  const [selectedFileType, setSelectedFileType] = useState(searchParams.get('file_type') || '');
  const [selectedEcuType, setSelectedEcuType] = useState(searchParams.get('ecu_type') || '');
  const [brands, setBrands] = useState([]);
  const [ecuTypes, setEcuTypes] = useState([]);
  
  // Pagination - initialize from URL params
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Cart
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  
  // Checkout
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [orderComplete, setOrderComplete] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '50'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (selectedBrand) params.append('brand', selectedBrand);
      if (selectedFileType) params.append('file_type', selectedFileType);
      if (selectedEcuType) params.append('ecu_type', selectedEcuType);
      
      const response = await fetch(`${API_URL}/api/file-store/products?${params}`);
      if (!response.ok) throw new Error('Failed to fetch products');
      
      const data = await response.json();
      setProducts(data.products);
      setTotalPages(data.total_pages);
      setTotalProducts(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, selectedBrand, selectedFileType, selectedEcuType]);

  // Fetch brands and ECU types
  const fetchFilters = async () => {
    try {
      const [brandsRes, ecuRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/file-store/brands`),
        fetch(`${API_URL}/api/file-store/ecu-types`),
        fetch(`${API_URL}/api/file-store/stats`)
      ]);
      
      if (brandsRes.ok) {
        const data = await brandsRes.json();
        setBrands(data.brands);
      }
      
      if (ecuRes.ok) {
        const data = await ecuRes.json();
        setEcuTypes(data.ecu_types);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching filters:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchFilters();
  }, []);

  // Sync URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedBrand) params.set('brand', selectedBrand);
    if (selectedFileType) params.set('file_type', selectedFileType);
    if (selectedEcuType) params.set('ecu_type', selectedEcuType);
    if (page > 1) params.set('page', page.toString());
    
    // Update URL without triggering a navigation
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedBrand, selectedFileType, selectedEcuType, page, setSearchParams]);

  // Manage canonical tag directly in DOM (react-helmet-async has issues with link tags)
  useEffect(() => {
    // Generate canonical URL based on the active filter
    // Each filter type gets its own canonical URL
    let canonicalUrl = 'https://ecuflashservice.com/file-store';
    
    // Only one primary filter should define the canonical
    // Priority: brand > ecu_type > file_type > base
    if (selectedBrand && !searchQuery) {
      canonicalUrl = `https://ecuflashservice.com/file-store?brand=${encodeURIComponent(selectedBrand)}`;
    } else if (selectedEcuType && !searchQuery) {
      canonicalUrl = `https://ecuflashservice.com/file-store?ecu_type=${encodeURIComponent(selectedEcuType)}`;
    } else if (selectedFileType && !searchQuery) {
      canonicalUrl = `https://ecuflashservice.com/file-store?file_type=${encodeURIComponent(selectedFileType)}`;
    }
    // Note: search queries don't get canonical URLs (too dynamic)
    
    // Remove any existing canonical link
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    // Add new canonical link
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = canonicalUrl;
    document.head.appendChild(link);
    
    // Cleanup on unmount
    return () => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.remove();
      }
    };
  }, [selectedBrand, searchQuery, selectedFileType, selectedEcuType]);

  // Cart functions
  const addToCart = (product) => {
    if (cart.find(item => item.product_id === product.product_id)) return;
    
    setCart([...cart, {
      file_id: product.product_id,
      name: product.name,
      price: product.price,
      file_type: product.file_type,
      bucket: product.bucket,
      key: product.key,
      brand: product.brand,
      product_id: product.product_id
    }]);
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price, 0);

  // Checkout
  const handleCheckout = async () => {
    if (!customerEmail) {
      alert('Please enter your email address');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/file-store/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart,
          email: customerEmail
        })
      });
      
      if (!response.ok) throw new Error('Failed to create order');
      
      const data = await response.json();
      setOrderId(data.order_id);
    } catch (err) {
      alert('Failed to create order: ' + err.message);
    }
  };

  // PayPal handlers
  const createPayPalOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [{
        amount: {
          value: cartTotal.toFixed(2),
          currency_code: 'USD'
        },
        description: `ECU Files Order - ${cart.length} files`
      }]
    });
  };

  const onPayPalApprove = async (data, actions) => {
    setProcessingPayment(true);
    
    try {
      const details = await actions.order.capture();
      
      // Complete the order on backend
      const response = await fetch(
        `${API_URL}/api/file-store/complete-payment/${orderId}?payment_id=${details.id}&payment_method=paypal`,
        { method: 'POST' }
      );
      
      if (!response.ok) throw new Error('Failed to complete payment');
      
      const result = await response.json();
      setDownloadLinks(result.downloads);
      setOrderComplete(true);
      setCart([]);
    } catch (err) {
      alert('Payment processing failed: ' + err.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Reset filters and clear URL params
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedBrand('');
    setSelectedFileType('');
    setSelectedEcuType('');
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  // Generate dynamic meta description based on filters
  const getMetaDescription = () => {
    if (selectedBrand) {
      return `Download ${selectedBrand} ECU files. ${totalProducts.toLocaleString()}+ tuning files. Original stock & Stage 1-4 modified. Instant download. BMW, Mercedes, VW, Audi, Ford files.`;
    }
    if (selectedEcuType) {
      return `Download ${selectedEcuType} ECU tuning files. Stock original & modified files. Bosch, Siemens, Continental, Delphi ECU files. Instant download.`;
    }
    return `ECU tuning file store. ${totalProducts.toLocaleString()}+ stock & modified files. BMW, Mercedes, VW, Audi, Ford, Toyota. Original & Stage 1-4 tuned files. Instant download.`;
  };

  // Generate dynamic page title based on filters
  const getPageTitle = () => {
    if (selectedBrand) {
      return `${selectedBrand} ECU Files | Download Stock & Tuned Files | ${totalProducts.toLocaleString()}+ Files`;
    }
    if (selectedEcuType) {
      return `${selectedEcuType} ECU Files | Download Tuning Files | ECU Flash Service`;
    }
    return `ECU File Store | Download ${totalProducts.toLocaleString()}+ Tuning Files | Stock & Modified`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={getMetaDescription()} />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-cyan-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <Database className="h-8 w-8 text-cyan-400" />
              <div>
                <h1 className="text-xl font-bold text-white">ECU File Store</h1>
                <p className="text-xs text-gray-400">Professional Tuning Files</p>
              </div>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                ← Back to Main Site
              </Link>
              
              <button
                onClick={() => setShowCart(true)}
                className="relative flex items-center gap-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg transition-colors"
                data-testid="cart-button"
              >
                <ShoppingCart className="h-5 w-5" />
                <span>Cart</span>
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-cyan-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Banner */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
              <div className="flex items-center gap-3">
                <Database className="h-8 w-8 text-cyan-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_products?.toLocaleString()}</p>
                  <p className="text-sm text-gray-400">Total Files</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.stock_files?.toLocaleString()}</p>
                  <p className="text-sm text-gray-400">Stock Files</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
              <div className="flex items-center gap-3">
                <Cpu className="h-8 w-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.modified_files?.toLocaleString()}</p>
                  <p className="text-sm text-gray-400">Modified Files</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
              <div className="flex items-center gap-3">
                <Car className="h-8 w-8 text-orange-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.brands}</p>
                  <p className="text-sm text-gray-400">Brands</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Info */}
        <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-xl p-6 mb-8 border border-cyan-500/20">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-gray-400 text-sm">Stock Files</p>
              <p className="text-3xl font-bold text-green-400">${STOCK_PRICE.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Original ECU files</p>
            </div>
            <div className="h-12 w-px bg-gray-700 hidden md:block" />
            <div className="text-center">
              <p className="text-gray-400 text-sm">Modified Files</p>
              <p className="text-3xl font-bold text-purple-400">${MODIFIED_PRICE.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Stage tuned files</p>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-gray-800/50 rounded-xl p-6 mb-8 border border-gray-700/50">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by brand, model, ECU part number..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                data-testid="search-input"
              />
            </div>
            
            {/* Brand Filter */}
            <select
              value={selectedBrand}
              onChange={(e) => { setSelectedBrand(e.target.value); setPage(1); }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:border-cyan-500 outline-none"
              data-testid="brand-filter"
            >
              <option value="">All Brands</option>
              {brands.map(b => (
                <option key={b.brand} value={b.brand}>{b.brand} ({b.count})</option>
              ))}
            </select>
            
            {/* File Type Filter */}
            <select
              value={selectedFileType}
              onChange={(e) => { setSelectedFileType(e.target.value); setPage(1); }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:border-cyan-500 outline-none"
              data-testid="file-type-filter"
            >
              <option value="">All Types</option>
              <option value="stock">Stock ($7.50)</option>
              <option value="modified">Modified ($15)</option>
            </select>
            
            {/* ECU Type Filter */}
            <select
              value={selectedEcuType}
              onChange={(e) => { setSelectedEcuType(e.target.value); setPage(1); }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:border-cyan-500 outline-none"
              data-testid="ecu-type-filter"
            >
              <option value="">All ECU Types</option>
              {ecuTypes.map(e => (
                <option key={e.ecu_type} value={e.ecu_type}>{e.ecu_type} ({e.count})</option>
              ))}
            </select>
            
            {/* Reset */}
            {(searchQuery || selectedBrand || selectedFileType || selectedEcuType) && (
              <button
                onClick={resetFilters}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Results Info */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-400">
            Showing {products.length} of {totalProducts.toLocaleString()} files
          </p>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
            <span className="ml-3 text-gray-400">Loading files...</span>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-400">{error}</p>
            <button
              onClick={fetchProducts}
              className="mt-4 px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <Database className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No files found matching your criteria</p>
            <button
              onClick={resetFilters}
              className="mt-4 px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {products.map(product => (
              <div
                key={product.product_id}
                className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50 hover:border-cyan-500/50 transition-colors"
                data-testid={`product-${product.product_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        product.file_type === 'modified' 
                          ? 'bg-purple-500/20 text-purple-400' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {product.file_type === 'modified' ? 'Modified' : 'Stock'}
                      </span>
                      {product.tuning_stage && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-cyan-500/20 text-cyan-400">
                          {product.tuning_stage}
                        </span>
                      )}
                      <span className="text-gray-500 text-xs">{formatSize(product.size)}</span>
                    </div>
                    
                    <h3 className="text-white font-medium truncate" title={product.name}>
                      <Link 
                        to={`/file-store/product/${product.slug || product.product_id}`}
                        className="hover:text-cyan-400 transition-colors"
                        data-testid={`product-link-${product.product_id}`}
                      >
                        {product.name}
                      </Link>
                    </h3>
                    
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Car className="h-4 w-4" />
                        {product.brand}
                      </span>
                      {product.ecu_type && (
                        <span className="flex items-center gap-1">
                          <Cpu className="h-4 w-4" />
                          {product.ecu_type}
                        </span>
                      )}
                      {product.ecu_part_number && (
                        <span className="text-gray-500">
                          Part: {product.ecu_part_number}
                        </span>
                      )}
                      {product.toyota_part_number && (
                        <span className="text-gray-500">
                          Toyota: {product.toyota_part_number}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Price & Add to Cart */}
                  <div className="flex items-center gap-4">
                    <span className={`text-2xl font-bold ${
                      product.file_type === 'modified' ? 'text-purple-400' : 'text-green-400'
                    }`}>
                      ${product.price.toFixed(2)}
                    </span>
                    
                    {cart.find(item => item.product_id === product.product_id) ? (
                      <button
                        onClick={() => removeFromCart(product.product_id)}
                        className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg flex items-center gap-2"
                        data-testid={`remove-from-cart-${product.product_id}`}
                      >
                        <Check className="h-4 w-4" />
                        Added
                      </button>
                    ) : (
                      <button
                        onClick={() => addToCart(product)}
                        className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg flex items-center gap-2 transition-colors"
                        data-testid={`add-to-cart-${product.product_id}`}
                      >
                        <ShoppingCart className="h-4 w-4" />
                        Add to Cart
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            <span className="px-4 py-2 text-gray-400">
              Page {page} of {totalPages}
            </span>
            
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </main>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCart(false)} />
          
          <div className="relative w-full max-w-md bg-gray-900 h-full overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 p-4 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-cyan-400" />
                Cart ({cart.length})
              </h2>
              <button
                onClick={() => setShowCart(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6">
                    {cart.map(item => (
                      <div
                        key={item.product_id}
                        className="bg-gray-800/50 rounded-lg p-3 flex items-start justify-between"
                      >
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="text-white text-sm truncate">{item.name}</p>
                          <p className="text-gray-500 text-xs">{item.brand} • {item.file_type}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-cyan-400 font-medium">${item.price.toFixed(2)}</span>
                          <button
                            onClick={() => removeFromCart(item.product_id)}
                            className="p-1 hover:bg-gray-700 rounded transition-colors"
                          >
                            <X className="h-4 w-4 text-gray-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-800 pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-400">Total</span>
                      <span className="text-2xl font-bold text-white">${cartTotal.toFixed(2)}</span>
                    </div>
                    
                    <button
                      onClick={() => { setShowCart(false); setShowCheckout(true); }}
                      className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                      data-testid="checkout-button"
                    >
                      <CreditCard className="h-5 w-5" />
                      Proceed to Checkout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => !processingPayment && setShowCheckout(false)} />
          
          <div className="relative w-full max-w-lg bg-gray-900 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {orderComplete ? 'Order Complete!' : 'Checkout'}
              </h2>
              {!processingPayment && !orderComplete && (
                <button
                  onClick={() => setShowCheckout(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              )}
            </div>
            
            <div className="p-6">
              {orderComplete ? (
                /* Order Complete View */
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
                  <p className="text-gray-400 mb-6">Your order #{orderId} has been completed.</p>
                  
                  <div className="bg-gray-800/50 rounded-xl p-4 text-left mb-6">
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <Download className="h-5 w-5 text-cyan-400" />
                      Your Downloads
                    </h4>
                    <div className="space-y-2">
                      {downloadLinks.map((link, idx) => (
                        <a
                          key={idx}
                          href={`${API_URL}${link.download_url}`}
                          className="block p-3 bg-gray-900/50 rounded-lg hover:bg-gray-900 transition-colors"
                          data-testid={`download-link-${idx}`}
                        >
                          <p className="text-white text-sm truncate">{link.file_name}</p>
                          <p className="text-gray-500 text-xs">
                            Expires: {new Date(link.expires_at).toLocaleString()} • Max {link.max_downloads} downloads
                          </p>
                        </a>
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-gray-500 text-sm mb-4">
                    Download links have been sent to your email: {customerEmail}
                  </p>
                  
                  <button
                    onClick={() => {
                      setShowCheckout(false);
                      setOrderComplete(false);
                      setOrderId(null);
                      setDownloadLinks([]);
                    }}
                    className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : !orderId ? (
                /* Email Entry */
                <div>
                  <div className="mb-6">
                    <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                      <input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 outline-none"
                        data-testid="checkout-email"
                      />
                    </div>
                    <p className="text-gray-500 text-xs mt-2">Download links will be sent to this email</p>
                  </div>
                  
                  <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">Files ({cart.length})</span>
                      <span className="text-white">${cartTotal.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-2 mt-2 flex items-center justify-between">
                      <span className="text-white font-medium">Total</span>
                      <span className="text-xl font-bold text-cyan-400">${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleCheckout}
                    disabled={!customerEmail}
                    className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                    data-testid="continue-to-payment"
                  >
                    Continue to Payment
                  </button>
                </div>
              ) : (
                /* PayPal Payment */
                <div>
                  <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
                    <p className="text-gray-400 text-sm mb-1">Order #{orderId}</p>
                    <p className="text-2xl font-bold text-white">${cartTotal.toFixed(2)}</p>
                  </div>
                  
                  {processingPayment ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-4" />
                      <p className="text-gray-400">Processing payment...</p>
                    </div>
                  ) : (
                    <PayPalScriptProvider options={{ 
                      clientId: PAYPAL_CLIENT_ID,
                      currency: 'USD'
                    }}>
                      <PayPalButtons
                        style={{ layout: 'vertical', color: 'blue' }}
                        createOrder={createPayPalOrder}
                        onApprove={onPayPalApprove}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          alert('Payment failed. Please try again.');
                        }}
                      />
                    </PayPalScriptProvider>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
